<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Blog Posts" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <form class="position-relative table-src-form me-0" @submit.prevent>
            <input type="text" class="form-control" placeholder="Search blog posts..." v-model="searchQuery" />
            <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
          </form>
          <RouterLink to="/blog-posts/create" class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg">
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>Add New Article</span>
            </span>
          </RouterLink>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Card Design</th>
                  <th scope="col">Title</th>
                  <th scope="col">Category</th>
                  <th scope="col">Date</th>
                  <th scope="col">Read Time</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="8" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="filteredPosts.length === 0">
                  <td colspan="8" class="text-center py-4">No articles found.</td>
                </tr>
                <tr v-else v-for="p in filteredPosts" :key="p.post_id">
                  <td>#{{ p.post_id }}</td>
                  <td>
                    <div class="rounded-3 d-flex align-items-center justify-content-center text-white wh-40" :style="{ background: p.bg }">
                      <span class="material-symbols-outlined fs-20">{{ p.icon }}</span>
                    </div>
                  </td>
                  <td class="fw-medium text-dark" style="max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    {{ p.title }}
                  </td>
                  <td><span class="badge bg-secondary">{{ p.category }}</span></td>
                  <td class="text-body">{{ p.date }}</td>
                  <td class="text-body">{{ p.read_time }}</td>
                  <td>
                    <span class="badge p-2 fs-12 fw-normal" :class="p.is_active ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'">
                      {{ p.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/blog-posts/${p.post_id}/edit`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-body">edit</i>
                      </RouterLink>
                      <button @click="deletePost(p.post_id)" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-danger">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import blogPostService from '@/services/blogPostService';

export default defineComponent({
  name: 'BlogPostListPage',
  components: {
    PageTitle
  },
  setup() {
    const posts = ref<any[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const fetchPosts = async () => {
      loading.value = true;
      try {
        const response = await blogPostService.getAll();
        posts.value = response.data;
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        loading.value = false;
      }
    };

    const deletePost = async (id: number) => {
      if (confirm('Are you sure you want to delete this blog post?')) {
        try {
          await blogPostService.delete(id);
          fetchPosts();
        } catch (error) {
          console.error('Error deleting blog post:', error);
        }
      }
    };

    const filteredPosts = computed(() => {
      if (!searchQuery.value) return posts.value;
      const q = searchQuery.value.toLowerCase();
      return posts.value.filter(p => 
        p.title.toLowerCase().includes(q) || 
        p.category.toLowerCase().includes(q) || 
        p.excerpt.toLowerCase().includes(q)
      );
    });

    onMounted(() => {
      fetchPosts();
    });

    return {
      posts,
      loading,
      searchQuery,
      filteredPosts,
      deletePost
    };
  }
});
</script>

<style scoped>
.wh-40 {
  width: 40px;
  height: 40px;
}
</style>
